import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../config/api';
import toast from 'react-hot-toast';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  // Token is now handled by the api interceptor in config/api.js

  // Load user on app start
  useEffect(() => {
    const loadUser = async () => {
      if (token) {
        try {
          const response = await api.get('/api/auth/me');
          setUser(response.data.user);
        } catch (error) {
          // Don't logout on rate limit errors
          if (error.response?.status === 429) {
            console.warn('Rate limited while loading user, will retry');
            // Keep token and user, just don't update
          } else if (error.response?.status === 401) {
            // Only logout on actual auth failures
            console.error('Failed to load user (unauthorized):', error);
            localStorage.removeItem('token');
            setToken(null);
            setUser(null);
          } else {
            // Network errors or other issues - don't logout
            console.warn('Failed to load user (network error):', error);
          }
        }
      }
      setLoading(false);
    };

    loadUser();
  }, [token]);

  const login = async (email, password, phone = null) => {
    try {
      const requestData = phone ? { phone, password } : { email, password };
      const response = await api.post('/api/auth/login', requestData);
      const { token: newToken, user: userData } = response.data;
      
      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser(userData);
      
      toast.success('Login successful!');
      return { success: true };
    } catch (error) {
      // Handle rate limit errors specially
      if (error.response?.status === 429) {
        const retryAfter = error.response?.data?.retryAfter || 60;
        const message = `Too many login attempts. Please wait ${retryAfter} seconds before trying again.`;
        toast.error(message, { duration: 5000 });
        return { success: false, error: message, isRateLimit: true, retryAfter };
      }
      
      const message = error.response?.data?.message || 'Login failed';
      const errors = error.response?.data?.errors;
      if (errors && errors.length > 0) {
        toast.error(errors[0].msg || message);
      } else {
        toast.error(message);
      }
      return { success: false, error: message };
    }
  };

  const register = async (userData) => {
    console.log('Registering user with data:', userData);
    try {
      const response = await api.post('/api/auth/register', userData);
      console.log('Registration response:', response);
      const { token: newToken, user: newUser } = response.data;
      
      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser(newUser);
      
      toast.success('Registration successful!');
      return { success: true };
    } catch (error) {
      console.error('Registration error:', error);
      const message = error.response?.data?.message || 'Registration failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    toast.success('Logged out successfully');
  };

  const updateUser = (userData) => {
    setUser(prev => ({ ...prev, ...userData }));
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    updateUser,
    isAuthenticated: !!user,
    isRider: user?.role === 'rider',
    isPassenger: user?.role === 'passenger',
    isAdmin: user?.role === 'admin',
    isVerified: user?.verificationStatus === 'verified'
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
